import React from 'react';
import { Award, Target, Eye, Users, CheckCircle, TrendingUp } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-green-50 py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeInLeft">
              <h1 className="text-5xl md:text-6xl font-bold text-[#004534] mb-6 leading-tight">
                About Shri Durga Enterprises
              </h1>
              <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                For over two decades, we've been at the forefront of solar energy revolution in Karnataka
                and Goa. With 5000+ successful installations, we're committed to making clean 
                energy accessible to everyone.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                From small residential projects to large industrial installations, our expertise spans 
                across all sectors, delivering customized solutions that maximize efficiency and savings.
              </p>
            </div>
            <div className="relative animate-fadeInRight">
              <img
                src="https://images.unsplash.com/photo-1635424709870-cdc6e64f0e20?w=800"
                alt="Team Installing Solar Panels"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#22c55e] text-white p-6 rounded-2xl shadow-xl">
                <p className="text-4xl font-bold mb-1">13+</p>
                <p className="text-sm font-medium">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="bg-[#1e3a8a] w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-[#004534] mb-4">Our Mission</h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                To accelerate the adoption of solar energy by providing reliable, efficient, and affordable 
                solar solutions. We aim to reduce carbon footprint while helping our clients achieve energy 
                independence and significant cost savings. Our mission is to contribute meaningfully to 
                India's Green Energy Mission and create a sustainable future for generations to come.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="bg-[#22c55e] w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-[#004534] mb-4">Our Vision</h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                To become the most trusted solar energy solutions provider across India, known for innovation, 
                quality, and exceptional customer service. We envision a future where every building is powered 
                by clean solar energy, and we're committed to making this vision a reality through continuous 
                improvement and technological advancement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Journey */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600">
              Two decades of innovation and excellence
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {[
                {
                  year: '2012',
                  title: 'Foundation',
                  description: 'Started with a vision to bring solar energy to Belagavi region. Initial focus on solar water heaters for residential customers.',
                },
                {
                  year: '2015',
                  title: 'Expansion',
                  description: 'Expanded services to commercial and industrial sectors. Reached 1000+ installations milestone.',
                },
                {
                  year: '2018',
                  title: 'Growth',
                  description: 'Extended operations to Goa. Introduced rooftop solar panels and heat pump systems.',
                },
                {
                  year: '2021',
                  title: 'Innovation',
                  description: 'Adopted advanced solar technologies. Launched agricultural solar pump solutions.',
                },
                {
                  year: '2025',
                  title: 'Excellence',
                  description: 'Completed 5000+ projects. Saved 2,75000 tons of CO₂ emissions. Leading solar solutions provider in the region.',
                },
              ].map((milestone, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-6 bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="bg-gradient-to-br from-[#1e3a8a] to-[#22c55e] text-white font-bold text-2xl w-20 h-20 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    {milestone.year}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#004534] mb-2">{milestone.title}</h3>
                    <p className="text-gray-700 leading-relaxed">{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600">
              Principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: 'Quality Excellence',
                description: 'We never compromise on quality. Every product and service meets the highest industry standards.',
              },
              {
                icon: Users,
                title: 'Customer First',
                description: 'Your satisfaction is our priority. We listen, understand, and deliver exactly what you need.',
              },
              {
                icon: CheckCircle,
                title: 'Integrity',
                description: 'Honest, transparent, and ethical in all our dealings. We build trust through our actions.',
              },
              {
                icon: TrendingUp,
                title: 'Innovation',
                description: 'Constantly evolving and adopting latest technologies to provide best-in-class solutions.',
              },
              {
                icon: Target,
                title: 'Sustainability',
                description: 'Committed to environmental protection and promoting renewable energy adoption.',
              },
              {
                icon: Eye,
                title: 'Reliability',
                description: 'Dependable service and support. We are there when you need us, always.',
              },
            ].map((value, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="bg-gradient-to-br from-[#1e3a8a] to-[#22c55e] w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-md">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#004534] mb-3">{value.title}</h3>
                <p className="text-gray-700 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics Highlight */}
      <section className="py-20 bg-gradient-to-r from-[#004534] to-[#0C6951] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="solar-grid-bg w-full h-full"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our Impact
            </h2>
            <p className="text-xl text-gray-200">
              Making a difference, one installation at a time
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '5000+', label: 'Projects Completed' },
              { number: '2,75000', label: 'Tons CO₂ Reduced' },
              { number: '2', label: 'States Served' },
              { number: '100%', label: 'Customer Satisfaction' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl md:text-6xl font-bold text-[#D3FF62] mb-2">
                  {stat.number}
                </div>
                <p className="text-gray-200 text-lg font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
        <style jsx>{`
          .solar-grid-bg {
            background-image: 
              linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px),
              linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px);
            background-size: 80px 80px;
          }
        `}</style>
      </section>

      <style jsx>{`
        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-fadeInLeft {
          animation: fadeInLeft 1s ease-out;
        }
        .animate-fadeInRight {
          animation: fadeInRight 1s ease-out;
        }
      `}</style>
    </div>
  );
};

export default About;
